/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { prefersReducedMotion } from '@theme/utilities';

/** @type {(() => void)[]} */
const youtubeReadyCallbacks = [];

/**
 * @returns {Promise<void>}
 */
function loadYouTubeAPI() {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }

    youtubeReadyCallbacks.push(resolve);

    if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) return;

    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === 'function') previousReady();
      while (youtubeReadyCallbacks.length) youtubeReadyCallbacks.shift()?.();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
}

class VideoControls extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {YT.Player | null} */
  #youtubePlayer = null;

  /** @type {HTMLIFrameElement | null} */
  #vimeoIframe = null;

  /** @type {'html5' | 'youtube' | 'vimeo'} */
  #provider = 'html5';

  /** @type {HTMLButtonElement | null} */
  #playButton = null;

  /** @type {HTMLButtonElement | null} */
  #muteButton = null;

  /** @type {HTMLElement | null} */
  #progress = null;

  #abortController = new AbortController();

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number} */
  #progressFrame = 0;

  /** @type {boolean} */
  #isPlaying = false;

  /** @type {boolean} */
  #isMuted = true;

  /** @type {number} */
  #vimeoDuration = 0;

  /** @type {IntersectionObserver | null} */
  #autoplayObserver = null;

  /** @type {boolean} */
  #autoplayPending = false;

  /** @type {boolean} */
  #posterGateOpen = false;

  /** @type {boolean} */
  #wantsPlayback = false;

  /** @type {boolean} */
  #sourceAttached = false;

  connectedCallback() {
    this.#playButton = this.querySelector('[data-video-control="play"]');
    this.#muteButton = this.querySelector('[data-video-control="mute"]');
    this.#progress = this.querySelector('[data-video-progress]');
    this.#bindMedia(this.querySelector('video') || this.querySelector('iframe'));
    this.#watchPoster();

    if (prefersReducedMotion()) {
      this.#video?.pause();
      this.#video?.removeAttribute('autoplay');
      return;
    }

    this.#observeAutoplay();

    if (!this.#video && !this.#youtubePlayer && !this.#vimeoIframe) {
      this.#observer = new MutationObserver(() => {
        const media = this.querySelector('video') || this.querySelector('iframe');
        if (media) {
          this.#bindMedia(media);
          this.#observer?.disconnect();
          this.#observer = null;
        }
      });
      this.#observer.observe(this, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#stopProgressLoop();
    this.#autoplayObserver?.disconnect();
    this.#abortController.abort();
    this.#observer?.disconnect();
  }

  #observeAutoplay() {
    if (!this.hasAttribute('data-autoplay') || prefersReducedMotion()) return;
    if (this.#video && !this.#video.paused) return;

    this.#autoplayPending = true;
    this.#autoplayObserver = new IntersectionObserver(
      (entries) => {
        if (!this.#autoplayPending || !entries.some((entry) => entry.isIntersecting)) return;

        this.#autoplayObserver?.disconnect();
        this.#autoplayObserver = null;
        this.#autoplayPending = false;
        this.#wantsPlayback = true;

        if (this.#posterGateOpen) this.#playFromAutoplay();
      },
      { rootMargin: '200px 0px', threshold: 0 },
    );
    this.#autoplayObserver.observe(this);
  }

  /**
   * Holds preload="none" until the poster has loaded. A cached image still
   * waits for a frame so the video request does not start in the same turn.
   */
  #watchPoster() {
    const video = this.#video;

    if (video instanceof HTMLVideoElement && !video.getAttribute('poster')) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const viewportPoster = isMobile ? video.dataset.posterMobile : video.dataset.posterDesktop;
      if (viewportPoster) video.poster = viewportPoster;
    }

    const posterUrl = video?.getAttribute('poster');

    if (!posterUrl) {
      this.#openPosterGate();
      return;
    }

    const signal = this.#abortController.signal;
    const image = new Image();
    image.src = posterUrl;

    const painted = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#openPosterGate());
      });
    };

    if (image.complete) {
      painted();
      return;
    }

    image.addEventListener('load', painted, { once: true, signal });
    image.addEventListener('error', () => this.#openPosterGate(), { once: true, signal });
  }

  #openPosterGate() {
    if (this.#posterGateOpen) return;

    this.#posterGateOpen = true;

    if (this.#wantsPlayback) this.#playFromAutoplay();
  }

  /**
   * Copies only the source list for the current viewport into the video.
   * URLs stay in inert templates until then, so a hidden breakpoint cannot download.
   */
  #attachViewportSource() {
    if (this.#sourceAttached || !(this.#video instanceof HTMLVideoElement)) return;

    const preferred = window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
    const template =
      this.querySelector(`template[data-video-sources="${preferred}"]`) ||
      this.querySelector('template[data-video-sources]');

    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = template.content.cloneNode(true);
    this.#video.replaceChildren(...fragment.childNodes);
    // Safari 16 ignores source elements added after the video was parsed until load() runs.
    this.#video.load();
    this.#sourceAttached = true;
  }

  #startHtml5Playback() {
    if (!this.#video) return;

    const video = this.#video;
    const signal = this.#abortController.signal;

    this.#attachViewportSource();

    const play = () => {
      if (signal.aborted || !video.paused) return;

      const playback = video.play();

      if (!playback || typeof playback.catch !== 'function') return;

      playback.catch(() => {
        if (signal.aborted || !video.paused) return;

        video.addEventListener(
          'canplay',
          () => {
            if (signal.aborted || !video.paused) return;
            video.play()?.catch(() => {});
          },
          { once: true, signal },
        );
      });
    };

    play();
  }

  #cancelPendingAutoplay() {
    this.#autoplayPending = false;
    this.#autoplayObserver?.disconnect();
    this.#autoplayObserver = null;
  }

  #playFromAutoplay() {
    if (this.#video) {
      this.#startHtml5Playback();
      return;
    }

    this.#playEmbed();
  }

  /**
   * @param {Element | null} media
   */
  #bindMedia(media) {
    if (media instanceof HTMLVideoElement) {
      this.#bindVideo(media);
      return;
    }

    if (!(media instanceof HTMLIFrameElement)) return;

    const type =
      media.dataset.videoType ||
      (media.classList.contains('js-youtube') ? 'youtube' : media.classList.contains('js-vimeo') ? 'vimeo' : '');

    if (type === 'youtube') {
      this.#setupYouTube(media);
      return;
    }

    if (type === 'vimeo') {
      this.#setupVimeo(media);
    }
  }

  /**
   * @param {HTMLVideoElement | null} video
   */
  #bindVideo(video) {
    if (!(video instanceof HTMLVideoElement) || this.#video === video) return;

    this.#provider = 'html5';
    this.#video = video;
    const signal = this.#abortController.signal;

    this.#playButton?.addEventListener('click', this.#togglePlay, { signal });
    this.#muteButton?.addEventListener('click', this.#toggleMute, { signal });
    this.#progress?.addEventListener('click', this.#seek, { signal });
    video.addEventListener('play', this.#syncPlay, { signal });
    video.addEventListener('pause', this.#syncPlay, { signal });
    video.addEventListener('ended', this.#syncPlay, { signal });
    video.addEventListener('volumechange', this.#syncMute, { signal });
    video.addEventListener('loadedmetadata', this.#syncProgress, { signal });

    this.#syncPlay();
    this.#syncMute();
    this.#syncProgress();
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #bindIframeControls() {
    const signal = this.#abortController.signal;
    this.#playButton?.addEventListener('click', this.#togglePlay, { signal });
    this.#muteButton?.addEventListener('click', this.#toggleMute, { signal });
    this.#progress?.addEventListener('click', this.#seek, { signal });
    this.#syncPlay();
    this.#syncMute();
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  async #setupYouTube(iframe) {
    this.#provider = 'youtube';
    this.#isPlaying = iframe.src.includes('autoplay=1');
    this.#isMuted = true;
    this.#bindIframeControls();

    await loadYouTubeAPI();
    if (!window.YT?.Player) return;

    if (!iframe.id) iframe.id = `VideoControls-${crypto.randomUUID()}`;

    this.#youtubePlayer = new window.YT.Player(iframe.id, {
      events: {
        onReady: (event) => {
          this.#isMuted = event.target.isMuted();
          this.#syncMute();
        },
        onStateChange: (event) => {
          this.#isPlaying = event.data === window.YT.PlayerState.PLAYING;
          this.#syncPlay();
        },
      },
    });
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #setupVimeo(iframe) {
    this.#provider = 'vimeo';
    this.#vimeoIframe = iframe;
    this.#isPlaying = iframe.src.includes('autoplay=1');
    this.#isMuted = true;
    this.#bindIframeControls();

    const signal = this.#abortController.signal;

    window.addEventListener(
      'message',
      (event) => {
        if (!this.#vimeoIframe || event.source !== this.#vimeoIframe.contentWindow) return;

        let payload = event.data;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch {
            return;
          }
        }

        if (payload.event === 'ready') {
          this.#postVimeo('addEventListener', 'play');
          this.#postVimeo('addEventListener', 'pause');
          this.#postVimeo('addEventListener', 'ended');
          this.#postVimeo('addEventListener', 'timeupdate');
          this.#postVimeo('getDuration');
        }

        if (payload.event === 'play') {
          this.#isPlaying = true;
          this.#syncPlay();
        }

        if (payload.event === 'pause' || payload.event === 'ended') {
          this.#isPlaying = false;
          this.#syncPlay();
        }

        if (payload.event === 'timeupdate' && payload.data) {
          this.#vimeoDuration = payload.data.duration || this.#vimeoDuration;
          this.#setProgress((payload.data.percent || 0) * 100);
        }

        if (payload.method === 'getDuration' && typeof payload.value === 'number') {
          this.#vimeoDuration = payload.value;
        }
      },
      { signal },
    );
  }

  #togglePlay = () => {
    if (this.#autoplayPending) {
      this.#cancelPendingAutoplay();
      this.#isPlaying = false;
      this.#syncPlay();
      return;
    }

    if (this.#provider === 'html5') {
      if (!this.#video) return;

      if (this.#video.paused) {
        this.#cancelPendingAutoplay();
        this.#posterGateOpen = true;
        this.#wantsPlayback = false;
        this.#startHtml5Playback();
      } else {
        this.#video.pause();
      }
      return;
    }

    if (this.#isPlaying) {
      this.#pauseEmbed();
    } else {
      this.#playEmbed();
    }
  };

  #toggleMute = () => {
    if (this.#provider === 'html5') {
      if (!this.#video) return;

      this.#video.muted = !this.#video.muted;

      if (!this.#video.muted && this.#video.volume === 0) {
        this.#video.volume = 1;
      }
      return;
    }

    this.#isMuted = !this.#isMuted;

    if (this.#youtubePlayer) {
      if (this.#isMuted) this.#youtubePlayer.mute();
      else this.#youtubePlayer.unMute();
    }

    if (this.#vimeoIframe) {
      this.#postVimeo('setVolume', this.#isMuted ? 0 : 1);
    }

    this.#syncMute();
  };

  /**
   * @param {MouseEvent} event
   */
  #seek = (event) => {
    if (!this.#progress) return;

    const rect = this.#progress.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));

    if (this.#video?.duration) {
      this.#video.currentTime = ratio * this.#video.duration;
      this.#syncProgress();
      return;
    }

    if (this.#youtubePlayer?.getDuration) {
      this.#youtubePlayer.seekTo(ratio * this.#youtubePlayer.getDuration(), true);
      this.#setProgress(ratio * 100);
      return;
    }

    if (this.#vimeoIframe && this.#vimeoDuration) {
      this.#postVimeo('setCurrentTime', ratio * this.#vimeoDuration);
      this.#setProgress(ratio * 100);
    }
  };

  #playEmbed() {
    this.#youtubePlayer?.playVideo?.();
    this.#postVimeo('play');
    this.#isPlaying = true;
    this.#syncPlay();
  }

  #pauseEmbed() {
    this.#youtubePlayer?.pauseVideo?.();
    this.#postVimeo('pause');
    this.#isPlaying = false;
    this.#syncPlay();
  }

  /**
   * @param {string} method
   * @param {unknown} [value]
   */
  #postVimeo(method, value) {
    this.#vimeoIframe?.contentWindow?.postMessage(JSON.stringify({ method, value }), 'https://player.vimeo.com');
  }

  #syncPlay = () => {
    const isPlaying = this.#provider === 'html5' ? Boolean(this.#video && !this.#video.paused) : this.#isPlaying;

    if (this.#playButton) {
      this.#playButton.querySelector('.icon-play')?.classList.toggle('hidden', isPlaying);
      this.#playButton.querySelector('.icon-pause')?.classList.toggle('hidden', !isPlaying);
    }

    if (isPlaying) {
      const deferredMedia = this.closest('deferred-media');
      deferredMedia?.classList.add('deferred-media--playing');
      deferredMedia?.querySelector('.deferred-media__poster-button')?.classList.add('deferred-media__playing');
      this.#startProgressLoop();
    } else {
      this.#stopProgressLoop();
      this.#syncProgress();
    }
  };

  #startProgressLoop() {
    this.#stopProgressLoop();
    this.#tickProgress();
  }

  #stopProgressLoop() {
    if (!this.#progressFrame) return;

    cancelAnimationFrame(this.#progressFrame);
    this.#progressFrame = 0;
  }

  #tickProgress = () => {
    this.#syncProgress();

    const isPlaying = this.#provider === 'html5' ? Boolean(this.#video && !this.#video.paused) : this.#isPlaying;

    if (isPlaying) {
      this.#progressFrame = requestAnimationFrame(this.#tickProgress);
    }
  };

  #syncMute = () => {
    if (!this.#muteButton) return;

    const isMuted =
      this.#provider === 'html5'
        ? Boolean(this.#video && (this.#video.muted || this.#video.volume === 0))
        : this.#isMuted;

    this.#muteButton.querySelector('.icon-volume')?.classList.toggle('hidden', isMuted);
    this.#muteButton.querySelector('.icon-volume-mute')?.classList.toggle('hidden', !isMuted);
  };

  #syncProgress = () => {
    if (!this.#progress) return;

    if (this.#video?.duration) {
      this.#setProgress((this.#video.currentTime / this.#video.duration) * 100);
      return;
    }

    if (this.#youtubePlayer?.getDuration && this.#youtubePlayer.getCurrentTime) {
      const duration = this.#youtubePlayer.getDuration();
      if (duration) this.#setProgress((this.#youtubePlayer.getCurrentTime() / duration) * 100);
    }
  };

  /**
   * @param {number} percent
   */
  #setProgress(percent) {
    if (!this.#progress) return;

    this.#progress.style.setProperty('--video-progress', `${percent}%`);
    this.#progress.setAttribute('aria-valuenow', String(Math.round(percent)));
  }
}

if (!customElements.get('video-controls')) {
  customElements.define('video-controls', VideoControls);
}
